/**
 * Agent Controls — where the owner decides how much the community manager may
 * do without them.
 *
 * TWO RULES SHAPE THIS PAGE.
 *
 * 1. THE BRAKE IS NEVER GATED. Stopping the agent — the kill switch, narrowing
 *    the mode, turning a channel off — is one tap with no dialog. Widening is
 *    the destructive direction here, so that is what confirms. This inverts the
 *    rule on the Jaguar-Owned controls page, where narrowing was the dangerous
 *    move, and the inversion is deliberate rather than an oversight.
 *
 * 2. EVERY CARD IS CAPPED AT 720px. The owner's display is 3440px wide.
 *    Uncapped cards throw the control to the far edge, and a kill-switch toggle
 *    was once reported as "missing" for exactly this reason.
 *
 * Also: App.useApp().modal, never the static Modal — the static one does not
 * mount under this app's React 19 setup, as jaguar_owned_controls/page.tsx
 * records.
 */
import { useEffect, useState } from 'react'
import { App, Button, Checkbox, Input, InputNumber, Segmented, Select, Switch, Tooltip } from 'antd'
import { Link } from 'react-router'
import { ListPage, SectionCard, Pill, type Stat } from '../../../theme/listkit'
import { supabaseClient } from '../../../lib/supabaseClient'
import { useCmSettings } from '../useCmSettings'
import { TypedConfirm } from './TypedConfirm'
import {
  AUTONOMOUS_PHRASE, CM_MODES, canPromoteTo, confirmPhraseFor, escalationFor,
  MODE_CATALOG, modeEntry, type PromotionState,
} from '../modes'
import { capsAllPositive, quietHoursSentence } from '../caps'
import type { CmMode } from '../types'

const CARD = { maxWidth: 720 } as const

const AUTO_APPROVE_CANDIDATES = [
  { key: 'mentorship_unanswered_48h', label: 'Nudge a member who was asked to mentor', note: 'one member, templated' },
  { key: 'rsvp_stale_48h', label: 'Ask about an open RSVP before an event', note: 'one member, templated' },
  { key: 'profile_incomplete', label: 'Ask a member to finish their profile', note: 'one member, templated' },
]

export function CmControlsPage() {
  const { settings, masterEnabled, isLoading, isError, refetch, save, setMaster } = useCmSettings()
  const { modal, message } = App.useApp()
  const [busy, setBusy] = useState(false)
  const [promo, setPromo] = useState<PromotionState | null>(null)

  // Re-read the evidence whenever the mode changes. An effect, not a memo:
  // this does I/O, and React may run a memo body any number of times.
  useEffect(() => { void loadPromotionState().then(setPromo) }, [settings?.mode])

  // The one confirmation that needs a typed phrase at a time.
  const [typedAction, setTypedAction] = useState<
    null | { kind: 'autonomous' } | { kind: 'clear'; n: number }
  >(null)

  const mode = settings?.mode ?? 'off'
  const stats: Stat[] = [
    { label: 'Mode', value: modeEntry(mode).title, tone: mode === 'off' ? 'neutral' : mode === 'autonomous' ? 'red' : 'gold' },
    { label: 'Agent', value: masterEnabled === null ? 'unknown' : masterEnabled ? 'running' : 'stopped',
      tone: masterEnabled ? 'green' : 'red' },
    { label: 'Channels on', value: String(
        [settings?.channel_inapp_enabled, settings?.channel_push_enabled, settings?.channel_feed_enabled]
          .filter(Boolean).length) },
    { label: 'Cap / member / week', value: String(settings?.max_per_member_per_week ?? '—') },
  ]

  async function guarded(fn: () => Promise<void>) {
    setBusy(true)
    try { await fn(); message.success('Saved') }
    catch (e) { message.error(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  // ── mode ──
  function onModeChange(next: CmMode) {
    if (!settings || next === settings.mode) return
    const from = settings.mode
    const esc = escalationFor(from, next)
    const entry = modeEntry(next)

    // Retreat is instant: no dialog stands between the owner and stopping.
    if (esc === 'none') { void guarded(() => save({ mode: next })); return }

    const gate = canPromoteTo(next, promo ?? emptyPromotion())
    if (!gate.ok) {
      modal.info({
        title: `Not yet ready for ${entry.title}`,
        content: (
          <div>
            <p style={{ marginTop: 0 }}>Before the agent can run in {entry.title}:</p>
            <ul style={{ paddingLeft: 18 }}>{gate.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
          </div>
        ),
      })
      return
    }

    if (esc === 'typed') {
      setTypedAction({ kind: 'autonomous' })
      return
    }

    modal.confirm({
      title: `Switch the agent to ${entry.title}?`,
      width: 560,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>{entry.what}</p>
          <p style={{ marginBottom: 0 }}><strong>What changes now:</strong> {entry.onFlip}</p>
          {next === 'tiered' && (
            <p style={{ marginBottom: 0, marginTop: 10 }}>
              Auto-sending: {settings.auto_approve_kinds.length === 0
                ? 'nothing yet — pick at least one kind below.'
                : settings.auto_approve_kinds.join(', ')}. Caps: {settings.max_per_member_per_day}/day
              and {settings.max_per_member_per_week}/week per member.
            </p>
          )}
        </div>
      ),
      okText: `Switch to ${entry.title}`,
      onOk: () => guarded(() => save({ mode: next })),
    })
  }

  // ── channels ──
  function onChannel(key: 'channel_inapp_enabled' | 'channel_push_enabled' | 'channel_feed_enabled', on: boolean, label: string) {
    // Off is a brake: immediate.
    if (!on) { void guarded(() => save({ [key]: false } as never)); return }
    modal.confirm({
      title: `Turn on ${label}?`,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>{channelOnNote(key)}</p>
          <p style={{ marginBottom: 0 }}>Anything already approved and waiting for this channel goes out on the next run.</p>
        </div>
      ),
      okText: 'Turn on',
      onOk: () => guarded(() => save({ [key]: true } as never)),
    })
  }

  if (isLoading) {
    return (
      <ListPage intro={{ eyebrow: 'Messaging · Community Manager', title: 'Controls', description: 'Loading…' }}>
        <SectionCard title="Loading" style={CARD}>Reading the agent settings…</SectionCard>
      </ListPage>
    )
  }
  if (isError || !settings) {
    return (
      <ListPage intro={{ eyebrow: 'Messaging · Community Manager', title: 'Controls', description: 'Could not read the agent settings.' }}>
        <SectionCard title="Unavailable" style={CARD}>
          <p>The settings row could not be read. The agent treats an unreadable mode as <strong>off</strong>, so nothing is being sent.</p>
          <Button onClick={() => void refetch()}>Try again</Button>
        </SectionCard>
      </ListPage>
    )
  }

  const queuedPromise = promo?.gradedCount ?? 0

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · Community Manager',
        title: 'Controls',
        description: 'How much the community manager may do without you. Stopping is always one tap; widening always asks.',
      }}
      stats={stats}
      statColumns={4}
    >
      {/* ── Emergency ─────────────────────────────────────────────── */}
      <SectionCard title="Stop the agent" style={{ ...CARD, borderColor: '#b9453b' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600 }}>
              {masterEnabled === null ? 'Unknown' : masterEnabled ? 'The agent is running' : 'The agent is stopped'}
            </div>
            <div style={{ opacity: 0.75, fontSize: 13 }}>
              Stopping takes effect immediately and needs no confirmation. Nothing is proposed and nothing is dispatched while it is off.
            </div>
          </div>
          <Switch
            data-testid="cm-master-switch"
            checked={masterEnabled === true}
            loading={busy}
            onChange={(on) => {
              if (!on) { void guarded(() => setMaster(false)); return }
              modal.confirm({
                title: 'Start the agent again?',
                content: `It will resume in ${modeEntry(mode).title}. ${modeEntry(mode).onFlip}`,
                okText: 'Start',
                onOk: () => guarded(() => setMaster(true)),
              })
            }}
          />
        </div>
      </SectionCard>

      {/* ── Mode ──────────────────────────────────────────────────── */}
      <SectionCard title="Mode" style={CARD}>
        <Segmented<CmMode>
          data-testid="cm-mode"
          block
          value={mode}
          disabled={busy}
          options={CM_MODES.map((m) => ({ label: modeEntry(m).title, value: m }))}
          onChange={onModeChange}
        />
        <div style={{ marginTop: 14 }}>
          {MODE_CATALOG.map((m) => (
            <div key={m.value} style={{ opacity: m.value === mode ? 1 : 0.45, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <strong>{m.title}</strong>
                {m.value === mode && <Pill tone="gold">current</Pill>}
                {!m.sends && <Pill tone="green">sends nothing</Pill>}
              </div>
              <div style={{ fontSize: 13 }}>{m.what}</div>
              {m.value === mode && (
                <div style={{ fontSize: 13, marginTop: 4 }}><em>{m.onFlip}</em></div>
              )}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
          Anything about money, bereavement or moderation always waits for you, in every mode — including Autonomous.
        </div>
      </SectionCard>

      {/* ── Channels ──────────────────────────────────────────────── */}
      <SectionCard
        title="Channels"
        sub="A channel that is off is off in every mode, including Autonomous. Switching one off holds anything already approved for it — nothing is lost, and it goes out when you switch it back on."
        style={CARD}
      >
        {([
          ['channel_inapp_enabled', 'In-app notifications', 'Appears in the member’s bell. Lowest risk.'],
          ['channel_push_enabled', 'Push notifications', 'Buzzes a phone. Delivery has never been verified in this app.'],
          ['channel_feed_enabled', 'Feed posts', 'Visible to the whole community at once.'],
        ] as const).map(([key, label, note]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '8px 0' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{label}</div>
              <div style={{ opacity: 0.75, fontSize: 13 }}>{note}</div>
            </div>
            <Switch
              data-testid={`cm-channel-${key}`}
              checked={settings[key]}
              disabled={busy}
              onChange={(on) => onChannel(key, on, label)}
            />
          </div>
        ))}
      </SectionCard>

      {/* ── Quiet hours ───────────────────────────────────────────── */}
      <SectionCard title="Quiet hours" style={CARD}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <InputNumber min={0} max={23} value={settings.quiet_hours_start} disabled={busy}
            onChange={(v) => v != null && void guarded(() => save({ quiet_hours_start: v }))} addonBefore="From" />
          <InputNumber min={0} max={23} value={settings.quiet_hours_end} disabled={busy}
            onChange={(v) => v != null && void guarded(() => save({ quiet_hours_end: v }))} addonBefore="Until" />
          <Select
            style={{ minWidth: 220 }}
            value={settings.community_timezone}
            disabled={busy}
            onChange={(v) => void guarded(() => save({ community_timezone: v }))}
            options={['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC']
              .map((t) => ({ label: t, value: t }))}
          />
        </div>
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13 }}>
          {quietHoursSentence(settings.quiet_hours_start, settings.quiet_hours_end, settings.community_timezone)}
        </p>
        <p style={{ marginBottom: 0, fontSize: 12, opacity: 0.7 }}>
          Work that comes up inside the window is held until it closes, not dropped.
        </p>
      </SectionCard>

      {/* ── Caps ──────────────────────────────────────────────────── */}
      <SectionCard title="How often it may speak" style={CARD}>
        {([
          ['max_per_member_per_day', 'Messages per member per day'],
          ['max_per_member_per_week', 'Messages per member per week'],
          ['max_broadcasts_per_week', 'Posts the whole community sees, per week'],
          ['max_sends_per_run', 'Hard ceiling on one run'],
          ['max_brain_proposals_per_day', 'Proposals the agent may file per day'],
        ] as const).map(([key, label]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '6px 0' }}>
            <div style={{ fontSize: 14 }}>{label}</div>
            <InputNumber
              data-testid={`cm-cap-${key}`}
              min={0} max={500} value={settings[key]} disabled={busy}
              onChange={(v) => {
                if (v == null) return
                if (v === 0) {
                  modal.confirm({
                    title: 'Set this to zero?',
                    content: 'Zero means the agent goes silent on this axis. That is a real setting, not a mistake — but nothing will go out.',
                    onOk: () => guarded(() => save({ [key]: 0 } as never)),
                  })
                  return
                }
                void guarded(() => save({ [key]: v } as never))
              }}
            />
          </div>
        ))}
        {!capsAllPositive(settings) && (
          <p style={{ marginBottom: 0, color: '#b9453b', fontSize: 13 }}>
            One of these is zero, so the agent cannot send. That blocks Tiered and Autonomous.
          </p>
        )}
      </SectionCard>

      {/* ── Auto-approve allowlist ────────────────────────────────── */}
      <SectionCard
        title="What may send itself"
        sub={mode === 'tiered' ? 'Used right now.' : 'Only used in Tiered mode.'}
        style={{ ...CARD, opacity: mode === 'tiered' ? 1 : 0.6 }}
      >
        {AUTO_APPROVE_CANDIDATES.map((c) => (
          <div key={c.key} style={{ padding: '6px 0' }}>
            <Checkbox
              data-testid={`cm-allow-${c.key}`}
              disabled={mode !== 'tiered' || busy}
              checked={settings.auto_approve_kinds.includes(c.key)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...settings.auto_approve_kinds, c.key]
                  : settings.auto_approve_kinds.filter((k) => k !== c.key)
                void guarded(() => save({ auto_approve_kinds: next }))
              }}
            >
              {c.label} <span style={{ opacity: 0.6, fontSize: 12 }}>({c.note})</span>
            </Checkbox>
          </div>
        ))}
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
          Only one-member, templated actions can appear here. Anything that reaches more than one person, or that the
          agent wrote itself, always waits for you.
        </div>
      </SectionCard>

      {/* ── Clear the queue ───────────────────────────────────────── */}
      <SectionCard title="Clear the queue" style={{ ...CARD, borderColor: '#b9453b' }}>
        <p style={{ marginTop: 0, fontSize: 13 }}>
          Rejects every proposal waiting for you. This cannot be undone — the agent will re-notice anything still true
          on its next run, but your queue history will show them rejected.
        </p>
        <Button danger disabled={busy} data-testid="cm-clear-queue" onClick={() => void countQueued().then((n) => {
          if (n === 0) message.info('Nothing is waiting.')
          else setTypedAction({ kind: 'clear', n })
        })}>
          Reject everything waiting
        </Button>
      </SectionCard>

      <TypedConfirm
        open={typedAction?.kind === 'autonomous'}
        title="Let the agent send on its own?"
        body={<p style={{ marginTop: 0 }}>{modeEntry('autonomous').onFlip}</p>}
        phrase={AUTONOMOUS_PHRASE}
        okText="Go autonomous"
        onConfirm={() => guarded(() => save({ mode: 'autonomous' }))}
        onClose={() => setTypedAction(null)}
      />
      <TypedConfirm
        open={typedAction?.kind === 'clear'}
        title={`Reject ${typedAction?.kind === 'clear' ? typedAction.n : 0} waiting proposals?`}
        body={<p style={{ marginTop: 0 }}>This cannot be undone.</p>}
        phrase={confirmPhraseFor(typedAction?.kind === 'clear' ? typedAction.n : 0)}
        okText="Reject them"
        onConfirm={async () => {
          const { ok, failed } = await rejectAllWaiting()
          message[failed ? 'warning' : 'success'](
            failed ? `Rejected ${ok}, but ${failed} failed.` : `Rejected ${ok}.`,
          )
          await refetch()
        }}
        onClose={() => setTypedAction(null)}
      />

      <div style={{ ...CARD, marginTop: 10, fontSize: 13 }}>
        <Link to="/audit_log">See who changed these</Link>
        {queuedPromise > 0 && <span style={{ opacity: 0.7 }}> · {queuedPromise} proposals marked so far</span>}
      </div>
    </ListPage>
  )
}

function emptyPromotion(): PromotionState {
  return {
    gradedCount: 0, thumbsUpRate: 0, daysInCurrentMode: 0, rejectionsLast14d: 0,
    autoApproveKinds: [], anyChannelOn: false, capsAllPositive: false, quietHoursSet: false,
    dispatchedLowTier: 0, uneditedRate: 0, hasMeasuredDelivery: false,
  }
}

/** Reads the evidence the promotion gate needs. Best effort: a failed read
 *  leaves a blocker in place rather than removing one. */
async function loadPromotionState(): Promise<PromotionState> {
  const s = emptyPromotion()
  try {
    const [{ data: settings }, { data: grades }, { data: rejections }, { data: sentLow }] = await Promise.all([
      supabaseClient.from('cm_settings').select('*').eq('id', true).maybeSingle(),
      supabaseClient.from('cm_proposal_grades').select('grade'),
      supabaseClient.from('cm_proposals').select('id')
        .eq('status', 'rejected')
        .gte('decided_at', new Date(Date.now() - 14 * 864e5).toISOString()),
      supabaseClient.from('cm_proposals').select('id, edited_payload')
        .in('status', ['sent', 'partially_sent']).lte('risk_tier', 1),
    ])
    if (settings) {
      s.autoApproveKinds = settings.auto_approve_kinds ?? []
      s.anyChannelOn = !!(settings.channel_inapp_enabled || settings.channel_push_enabled || settings.channel_feed_enabled)
      s.capsAllPositive = capsAllPositive(settings)
      s.quietHoursSet = settings.quiet_hours_start !== settings.quiet_hours_end && !!settings.community_timezone
      // mode_changed_at, NOT updated_at: tuning a cap must not restart the
      // clock that decides when Autonomous unlocks.
      s.daysInCurrentMode = settings.mode_changed_at
        ? Math.floor((Date.now() - Date.parse(settings.mode_changed_at)) / 864e5) : 0
    }
    const g = grades ?? []
    s.gradedCount = g.length
    s.thumbsUpRate = g.length ? g.filter((x) => x.grade === 'up').length / g.length : 0
    s.rejectionsLast14d = rejections?.length ?? 0
    const low = sentLow ?? []
    s.dispatchedLowTier = low.length
    s.uneditedRate = low.length ? low.filter((x) => !x.edited_payload).length / low.length : 0
    // A delivery receipt Expo actually confirmed, from push-receipts, in the
    // last 30 days. This proves push reaches a phone at all — the precondition
    // for trusting anything to go out unattended. Until the first 'ok' receipt
    // exists, Autonomous stays locked.
    const { count: receipts } = await supabaseClient
      .from('push_tickets').select('id', { count: 'exact', head: true })
      .eq('receipt_status', 'ok')
      .gte('checked_at', new Date(Date.now() - 30 * 864e5).toISOString())
    s.hasMeasuredDelivery = (receipts ?? 0) > 0
  } catch { /* leave the blockers in place */ }
  return s
}

async function countQueued(): Promise<number> {
  const { count } = await supabaseClient
    .from('cm_proposals').select('id', { count: 'exact', head: true }).eq('status', 'pending_review')
  return count ?? 0
}

/**
 * Rejects every waiting proposal through the Edge Function, one at a time.
 * Sequential on purpose: a shared mutation observer drops the earlier callbacks
 * when a second call starts mid-flight, which is how a bulk action ends up
 * silently half-applied.
 */
async function rejectAllWaiting(): Promise<{ ok: number; failed: number }> {
  const { data: rows } = await supabaseClient
    .from('cm_proposals').select('id').eq('status', 'pending_review')
  let ok = 0, failed = 0
  for (const r of rows ?? []) {
    const { error } = await supabaseClient.functions.invoke('community-decide', {
      body: { proposalId: r.id, action: 'reject', note: 'cleared from Controls' },
    })
    if (error) failed++; else ok++
  }
  return { ok, failed }
}

/** What switching a channel ON means for a member, stated before it happens. */
function channelOnNote(key: 'channel_inapp_enabled' | 'channel_push_enabled' | 'channel_feed_enabled'): string {
  switch (key) {
    case 'channel_push_enabled':
      return 'Push has never been proven to deliver in this app — no delivery receipt has ever been recorded. Turning it on does not break anything, but do not read a "sent" count as proof that anyone received it.'
    case 'channel_feed_enabled':
      return 'Feed posts are visible to the whole community at once. They can be deleted afterwards, but everyone will have seen them.'
    default:
      return 'In-app notifications appear in the bell. This is the lowest-risk channel.'
  }
}
